import { auth0Config } from '../../../config/auth0'

export type LogoutStatus = {
  platform: string
  status: 'loading' | 'ready' | 'success' | 'error' | 'timeout'
  message?: string
}

type ReadyResolver = {
  resolve: () => void
  timeout: ReturnType<typeof setTimeout>
}

export class UniversalLogoutOrchestrator {
  private iframes: Map<string, HTMLIFrameElement> = new Map()
  private statuses: Map<string, LogoutStatus> = new Map()
  private readyResolvers: Map<string, ReadyResolver> = new Map()
  private logoutResolvers: Map<string, ReadyResolver> = new Map()
  private messageHandler: (event: MessageEvent) => void

  constructor(
    private onStatusChange: (statuses: LogoutStatus[]) => void,
    private platforms: Record<string, string> = {}
  ) {
    this.messageHandler = this.handleMessage.bind(this)
    window.addEventListener('message', this.messageHandler)
  }

  async executeLogout(): Promise<void> {
    console.log('[Orchestrator] Starting executeLogout')

    // 1. Create iframes for each platform
    console.log('[Orchestrator] Creating iframes for platforms:', Object.keys(this.platforms))
    this.createIframes()

    // 2. Wait for all platforms to be ready (with timeout)
    console.log('[Orchestrator] Waiting for platforms to be ready...')
    await this.waitForReady()
    console.log('[Orchestrator] All platforms ready check complete')

    // 3. Send logout command to all platforms
    console.log('[Orchestrator] Sending logout commands...')
    await this.sendLogoutCommands()
    console.log('[Orchestrator] All logout commands complete')

    // 4. Clean up
    console.log('[Orchestrator] Cleaning up iframes')
    this.cleanup()
    console.log('[Orchestrator] executeLogout complete')
  }

  private createIframes() {
    for (const [key, url] of Object.entries(this.platforms)) {
      console.log(`[Orchestrator] Creating iframe for ${key} at ${url}`)
      const iframe = document.createElement('iframe')
      iframe.src = url
      iframe.style.display = 'none'
      iframe.setAttribute('aria-hidden', 'true')
      document.body.appendChild(iframe)

      this.iframes.set(key, iframe)
      this.statuses.set(key, { platform: key, status: 'loading' })
    }
    this.notifyStatusChange()
  }

  private async waitForReady(): Promise<void> {
    // TIMEOUT DISABLED FOR DEBUGGING
    const readyPromises = Array.from(this.iframes.keys()).map((platform) => {
      return new Promise<void>((resolve) => {
        // No timeout - will wait indefinitely
        this.readyResolvers.set(platform, {
          resolve: () => {
            this.readyResolvers.delete(platform)
            resolve()
          },
          timeout: null as unknown as ReturnType<typeof setTimeout> // No timeout
        })
      })
    })

    // Just wait for child iframes to proactively send PP_SSO_READY
    console.log('[Orchestrator] Waiting for platforms to send PP_SSO_READY... (NO TIMEOUT)')
    await Promise.all(readyPromises)
  }

  private async sendLogoutCommands(): Promise<void> {
    const readyPlatforms = Array.from(this.iframes.entries()).filter(([platform]) => {
      const status = this.statuses.get(platform)
      return status?.status === 'ready'
    })

    console.log(`[Orchestrator] Sending logout to ${readyPlatforms.length} ready platforms`)

    // TIMEOUT DISABLED FOR DEBUGGING
    const logoutPromises = readyPlatforms.map(([platform, iframe]) => {
      return new Promise<void>((resolve) => {
        // No timeout - will wait indefinitely
        this.logoutResolvers.set(platform, {
          resolve: () => {
            this.logoutResolvers.delete(platform)
            resolve()
          },
          timeout: null as unknown as ReturnType<typeof setTimeout> // No timeout
        })

        console.log(`[Orchestrator] Sending PP_SSO_LOGOUT to ${platform}`)
        iframe.contentWindow?.postMessage({ type: 'PP_SSO_LOGOUT' }, this.platforms[platform])
      })
    })

    await Promise.all(logoutPromises)
  }

  private handleMessage(event: MessageEvent) {
    const { type, platform } = event.data
    console.log('[Orchestrator] Received message:', { type, platform, origin: event.origin }, event)

    // Only process PP_SSO messages
    if (!type?.startsWith('PP_SSO_')) {
      console.log(`[Orchestrator] Ignoring non-PP_SSO message: ${type}`)
      return
    }

    if (type === 'PP_SSO_READY') {
      console.log(`[Orchestrator] Platform ${platform} is ready`)
      this.updateStatus(platform, 'ready')
      this.readyResolvers.get(platform)?.resolve()
    }

    if (type === 'PP_SSO_LOGOUT_SUCCESS') {
      console.log(`[Orchestrator] Platform ${platform} logout successful`)
      this.updateStatus(platform, 'success')
      this.logoutResolvers.get(platform)?.resolve()
    }
  }

  private updateStatus(platform: string, status: LogoutStatus['status'], message?: string) {
    this.statuses.set(platform, { platform, status, message })
    this.notifyStatusChange()
  }

  private notifyStatusChange() {
    this.onStatusChange(Array.from(this.statuses.values()))
  }

  cleanup() {
    window.removeEventListener('message', this.messageHandler)
    for (const iframe of this.iframes.values()) {
      iframe.remove()
    }
    this.iframes.clear()

    // Clear any pending timeouts
    for (const resolver of this.readyResolvers.values()) {
      clearTimeout(resolver.timeout)
    }
    for (const resolver of this.logoutResolvers.values()) {
      clearTimeout(resolver.timeout)
    }
    this.readyResolvers.clear()
    this.logoutResolvers.clear()
  }
}

// Silent Auth0 logout via hidden iframe
export async function silentAuth0Logout(): Promise<void> {
  return new Promise((resolve) => {
    const logoutUrl = `${auth0Config.discoveryUrl}/v2/logout?client_id=${auth0Config.clientId}`

    const iframe = document.createElement('iframe')
    iframe.src = logoutUrl
    iframe.style.display = 'none'
    iframe.setAttribute('aria-hidden', 'true')
    document.body.appendChild(iframe)

    // Auth0 logout is fast, give it 2 seconds max
    setTimeout(() => {
      iframe.remove()
      resolve()
    }, 2000)
  })
}
